import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';

const OUTCOMES = ['', 'ROSC', 'death', 'timeout', 'aborted'];
const REGIONS = ['', 'UK_RCUK_2021', 'US_AHA_2020', 'ERC_2021', 'ANZCOR_2021'];

export default function Runs() {
  const [runs, setRuns] = useState<any[] | null>(null);
  const [outcome, setOutcome] = useState('');
  const [region, setRegion] = useState('');
  const [sortBy, setSortBy] = useState<'started_at' | 'score' | 'time_to_first_shock_ms'>('started_at');

  useEffect(() => {
    api.adminRuns({ outcome: outcome || undefined, region: region || undefined, limit: 500 }).then((r) => {
      const sorted = [...r.runs].sort((a, b) => {
        const av = a[sortBy] ?? 0;
        const bv = b[sortBy] ?? 0;
        return sortBy === 'started_at' ? bv - av : av - bv;
      });
      setRuns(sorted);
    });
  }, [outcome, region, sortBy]);

  return (
    <div>
      <div className="flex justify-between items-end mb-4 flex-wrap gap-2">
        <h1 className="text-2xl font-semibold">Runs</h1>
        <a href={api.adminRunsCsvUrl()} target="_blank" rel="noreferrer" className="btn-ghost text-sm">📥 Export CSV</a>
      </div>

      <div className="panel p-3 mb-4 flex gap-3 flex-wrap text-sm">
        <Filter label="Outcome" value={outcome} setValue={setOutcome} options={OUTCOMES} />
        <Filter label="Region" value={region} setValue={setRegion} options={REGIONS} />
        <Filter label="Sort by" value={sortBy} setValue={(v) => setSortBy(v as any)} options={['started_at', 'score', 'time_to_first_shock_ms']} />
      </div>

      {runs == null && <p className="text-mono-white/60">Loading…</p>}
      {runs && runs.length === 0 && <p className="text-mono-white/60 panel p-4">No runs match the filters.</p>}
      {runs && runs.length > 0 && (
        <div className="panel overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-mono-white/40 bg-ink-800/50">
              <tr>
                <Th>When</Th>
                <Th>Learner</Th>
                <Th>Scenario</Th>
                <Th>Difficulty</Th>
                <Th>Outcome</Th>
                <Th>Score</Th>
                <Th>CCF%</Th>
                <Th>t-shock</Th>
                <Th>t-CPR</Th>
                <Th>Deviations</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className="border-t border-ink-800 hover:bg-ink-800/30">
                  <Td><span className="text-mono-white/60 text-xs">{r.started_at ? new Date(r.started_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '—'}</span></Td>
                  <Td>{r.learner || <span className="text-mono-white/40 italic">system</span>}</Td>
                  <Td><span className="text-mono-white/90">{r.scenario_title}</span><br /><span className="font-mono text-[10px] text-mono-white/40">{r.region}</span></Td>
                  <Td><span className="text-xs uppercase">{r.difficulty}</span></Td>
                  <Td><OutcomeBadge outcome={r.outcome} /></Td>
                  <Td><span className="font-mono">{r.score ?? '—'}</span></Td>
                  <Td><span className="font-mono">{r.ccf_pct != null ? `${r.ccf_pct.toFixed(0)}` : '—'}</span></Td>
                  <Td><span className="font-mono">{r.time_to_first_shock_ms != null ? `${(r.time_to_first_shock_ms / 1000).toFixed(0)}s` : '—'}</span></Td>
                  <Td><span className="font-mono">{r.time_to_cpr_ms != null ? `${(r.time_to_cpr_ms / 1000).toFixed(0)}s` : '—'}</span></Td>
                  <Td><span className={`font-mono ${r.protocol_deviations_count > 3 ? 'text-alarm-red' : r.protocol_deviations_count > 1 ? 'text-alarm-amber' : 'text-mono-green'}`}>{r.protocol_deviations_count ?? 0}</span></Td>
                  <Td><Link to={`/debrief/${r.id}`} className="text-brand-600 text-xs hover:underline">debrief →</Link></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Filter({ label, value, setValue, options }: { label: string; value: string; setValue: (v: string) => void; options: string[] }) {
  return (
    <label className="text-mono-white/60 text-xs flex items-center gap-2">
      {label}:
      <select value={value} onChange={(e) => setValue(e.target.value)} className="bg-ink-800 border border-ink-800 rounded px-2 py-1 text-mono-white text-sm focus:border-brand-600 focus:outline-none">
        {options.map((o) => <option key={o} value={o}>{o || '(any)'}</option>)}
      </select>
    </label>
  );
}

function Th({ children }: { children?: React.ReactNode }) { return <th className="text-left px-3 py-2 font-medium whitespace-nowrap">{children}</th>; }
function Td({ children }: { children?: React.ReactNode }) { return <td className="px-3 py-2 align-top">{children}</td>; }

function OutcomeBadge({ outcome }: { outcome: string | null }) {
  if (!outcome) return <span className="text-mono-white/40 text-xs">—</span>;
  const colors: Record<string, string> = {
    ROSC: 'bg-mono-green/20 text-mono-green',
    death: 'bg-alarm-red/20 text-alarm-red',
    timeout: 'bg-alarm-amber/20 text-alarm-amber',
    aborted: 'bg-ink-800 text-mono-white/60',
  };
  return <span className={`chip ${colors[outcome] || 'chip-off'} uppercase text-[10px] tracking-widest`}>{outcome}</span>;
}
